/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.1.0
 * @since 1.0.0
 */
import * as Yup from 'yup';

import { stringFormatParams } from '@/main/utils/stringFormat';
import { DEFAULT_MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '@components/Table/constants'
import { ICreateInquiryTranslation } from '@modules/internationalization/types/corporate';

import { ETopicAttrDataType, ETopicAttrVisibility, ITopicCustomerAttr } from './types/createInquiryTypes';

export const fieldValidation = (value: string, translation: ICreateInquiryTranslation) =>
    Yup.string()
        .required(translation.requiredField)
        .validate(value)
        .then(() => undefined)
        .catch(({ errors }) => errors[0]);

export const emailValidation = (value: string, translation: ICreateInquiryTranslation, regExp: string) =>
    Yup.string()
        .required(translation.requiredField)
        .matches(new RegExp(regExp), translation.incorrectEmail)
        .nullable()
        .validate(value)
        .then(() => undefined)
        .catch(({ errors }) => errors[0]);

const phoneRegExp = new RegExp('^(9)[0-9]{9}$');
export const phoneValidation = (value: string, translation: ICreateInquiryTranslation) =>
    Yup.string()
        .matches(phoneRegExp, { message: translation.incorrectPhone, excludeEmptyString: true })
        .validate(value)
        .then(() => undefined)
        .catch(({ errors }) => errors[0]);

export const attrFieldValidation = (
    value: string,
    translation: ICreateInquiryTranslation,
    attr: ITopicCustomerAttr,
) => {
    if (
        attr.declaration.visibility === ETopicAttrVisibility.MANDATORY
        && !(attr.declaration.dataType === ETopicAttrDataType.DICTIONARY
        && attr.declaration.multiselect === 0)
        && attr.declaration.regExpRule.regExpText
    ) {
        const regExp = new RegExp(attr.declaration.regExpRule.regExpText);
        return Yup.string()
                .required(translation.requiredField)
                .matches(regExp, attr.declaration.regExpRule.exceptionMessage)
                .validate(value)
                .then(() => undefined)
                .catch(({ errors }) => errors[0]);
    } else if (
        attr.declaration.visibility === ETopicAttrVisibility.MANDATORY
        && !(attr.declaration.dataType === ETopicAttrDataType.DICTIONARY
        && attr.declaration.multiselect === 0)
        && !attr.declaration.regExpRule.regExpText
    ) {
        return Yup.string()
                .required(translation.requiredField)
                .validate(value)
                .then(() => undefined)
                .catch(({ errors }) => errors[0]);
    } else if (
        attr.declaration.visibility !== ETopicAttrVisibility.MANDATORY
        && attr.declaration.regExpRule.regExpText
    ) {
        const regExp = new RegExp(attr.declaration.regExpRule.regExpText);
        return Yup.string()
                .matches(regExp, attr.declaration.regExpRule.exceptionMessage)
                .validate(value)
                .then(() => undefined)
                .catch(({ errors }) => errors[0]);
    }
    return Yup.string()
            .validate(value)
            .then(() => undefined)
            .catch(({ errors }) => errors[0]);
};

export const uploadFilesValidation = (files: FileList, translation: ICreateInquiryTranslation) => {
    let filesExceededError = '';

    const validatedFiles = Array.from(files).reduce((acc, currentFile) => {
        if (currentFile.size > DEFAULT_MAX_FILE_SIZE) {
            acc.inValidFiles.push(currentFile.name);
        } else {
            acc.validFiles.push(currentFile);
        }
        return acc;
    }, { validFiles: [] as File[], inValidFiles: [] as string[] });

    if (!!validatedFiles.inValidFiles.length) {
        const inValidFilesName = validatedFiles.inValidFiles.join('; ');

        filesExceededError = stringFormatParams(
            translation.filesExceededError, { filesName: inValidFilesName, sizeLimit: MAX_FILE_SIZE_MB },
        );
    }

    return {
        filesExceededError,
        validFiles: validatedFiles.validFiles,
    }
};
